import {z} from 'zod';
import {createForm} from '../../forms';
import {ActionIcon, Button, Divider, Group, Stack, Text} from '@mantine/core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDown, faTrash, faUp} from '@fortawesome/pro-solid-svg-icons';
import {nonEmptyString} from '../../utils/zod.ts';
import {OptionItemDto} from "../../api/generics.ts";

const TaskPriorities = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
} as const;

const TaskSchema = z.object({
  index: z.number(),
  title: nonEmptyString(),
  description: z.string().optional(),
  dueDate: z.date().optional(),
  priority: z.nativeEnum(TaskPriorities),
});

const TaskListSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  assignedTo: z.number().optional(),
  tasks: z.array(TaskSchema),
});
type TaskListSchema = z.TypeOf<typeof TaskListSchema>;

const UserOptions: OptionItemDto[] = [
  {value: 1, label: 'Alice'},
  {value: 2, label: 'Bob'},
  {value: 3, label: 'Charlie'},
  {value: 4, label: 'David'},
  {value: 5, label: 'Eve'},
];

const Form = createForm<TaskListSchema>();

export const FieldArrayDemo = () => {
  return (
    <Form schema={TaskListSchema} debug>
      <Form.Container>
        <Group justify={'space-between'} mb={'lg'}>
          <Text fw={600} fz={'lg'}>
            New Task List
          </Text>
          <Form.Submit>Create</Form.Submit>
        </Group>
        <Stack>
          <Form.TextInput
            name="name"
            label={'Name'}
            description={'Give your Task List a name'}
          />
          <Form.TextArea
            name="description"
            label={'Description'}
            description={'Add a description for your Task List (optional)'}
          />
          <Form.Select
            name="assignedTo"
            label={'Assigned User'}
            description={'Assign a User to your Task List (optional)'}
            data={UserOptions}
          />
          <Divider />
          <TaskFields />
        </Stack>
      </Form.Container>
    </Form>
  );
};

const TaskFields = () => {
  return (
    <Stack>
      <Text fw={600}>Tasks</Text>
      <Form.FieldArray name={'tasks'}>
        {({fields, append, ...actions}) => (
          <>
            {fields.map((field, index) => (
              <Form.Row
                key={field.id}
                wrap={'nowrap'}
                grow={false}
                align={'center'}
              >
                <Group grow wrap={'nowrap'} flex={1} align={'flex-start'}>
                  <Form.TextInput
                    name={`tasks.${index}.title`}
                    label={'Title'}
                  />
                  <Form.TextInput
                    name={`tasks.${index}.description`}
                    label={'Description'}
                  />
                  <Form.DatePickerInput
                    name={`tasks.${index}.dueDate`}
                    label={'Due Date'}
                  />
                  <Form.Select
                    name={`tasks.${index}.priority`}
                    label={'Priority'}
                    enumObject={TaskPriorities}
                  />
                </Group>
                <ActionIcon
                  disabled={index < 1}
                  onClick={() => actions.moveUp(index)}
                >
                  <FontAwesomeIcon icon={faUp} />
                </ActionIcon>
                <ActionIcon
                  disabled={index === fields.length - 1}
                  onClick={() => actions.moveDown(index)}
                >
                  <FontAwesomeIcon icon={faDown} />
                </ActionIcon>
                <ActionIcon onClick={() => actions.remove(index)}>
                  <FontAwesomeIcon icon={faTrash} />
                </ActionIcon>
              </Form.Row>
            ))}
            <Button
              type={'button'}
              onClick={() =>
                append({index: fields.length, title: '', priority: 'Low'})
              }
              mt={'md'}
            >
              Add Task
            </Button>
          </>
        )}
      </Form.FieldArray>
    </Stack>
  );
};